import { consoleEvents } from '@pb/utils'
import produce from 'immer'
import { mergeDeepRight, pathOr, path, lensPath, set, compose, equals, view } from 'ramda'
import { createReducer } from 'redux-act'

import * as actions from './actions'
import {
  FILTER_NAME,
  LOAD_MORE_ORDERS_PAGE_SIZE,
  MONTHLY_ORDER_LIST_INITIAL_SIZE,
  PAYABLE_MONTHLY_INVOICE_STATUSES,
} from './constants'
import { initialState } from './initialState'

// ----------------------------------------
// FETCH ORDERS
// ----------------------------------------
const fetchOrdersStart = (state, filterName): any => {
  const filterData = pathOr({}, ['orders', 'filters', filterName], state)

  return {
    ...state,
    orders: {
      ...state.orders,
      filters: {
        ...state.orders.filters,
        [filterName]: {
          ...filterData,
          isLoading: true,
        },
      },
    },
  }
}

const fetchOrdersSuccess = (
  state,
  { filterName, orders, totalNumberOfOrders, nbrOrdersInResponse },
): any => {
  const filterData = pathOr({}, ['orders', 'filters', filterName], state)
  return {
    ...state,
    orders: {
      ...state.orders,
      invoices: mergeDeepRight(
        state.orders.invoices,
        Object.assign(
          {},
          ...orders.map((order) => ({
            [order.orderId]: order,
          })),
        ),
      ),
      filters: {
        ...state.orders.filters,
        [filterName]: {
          ...filterData,
          orderIds: [
            ...pathOr([], ['orderIds'], filterData),
            ...orders.map((order) => order.orderId),
          ],
          isLoading: false,
          totalNumberOfOrders,
          nextOffset: pathOr(0, ['nextOffset'], filterData) + parseInt(nbrOrdersInResponse, 10),
          hasLoaded: true,
          error: false,
        },
      },
    },
  }
}

const fetchOrdersFail = (state, { filterName, error }): any => {
  consoleEvents.error(`Failed to load Klarna orders for filter ${filterName}: ${error}`)
  const filterData = pathOr({}, ['orders', 'filters', filterName], state)
  return {
    ...state,
    orders: {
      ...state.orders,
      filters: {
        ...state.orders.filters,
        [filterName]: {
          ...filterData,
          isLoading: false,
          error: true,
        },
      },
    },
    error: error.message,
  }
}

// ----------------------------------------
// FETCH ORDER
// ----------------------------------------
const fetchOrderStart = (state, orderId): any => ({
  ...state,
  loadingOrderId: orderId,
})

const fetchOrderSuccess = (state, order): any =>
  produce(state, (draftState) => {
    const componentState = draftState
    const currentInvoices = componentState.orders.invoices

    currentInvoices[order.orderId] = order
    componentState.loadingOrderId = ''
  })

const fetchOrderFail = (state, error): any => {
  consoleEvents.error(`Failed to update Klarna order: ${error}`)

  return {
    ...state,
    loadingOrderId: '',
  }
}

// ----------------------------------------
// LOAD MORE ORDERS
// ----------------------------------------
const loadMoreOrdersStart = (state, { filterName }): any => {
  const filterData = pathOr({}, ['orders', 'filters', filterName], state)

  return {
    ...state,
    orders: {
      ...state.orders,
      filters: {
        ...state.orders.filters,
        [filterName]: {
          ...filterData,
          isLoadingMore: true,
        },
      },
    },
  }
}

const loadMoreOrdersSuccess = (
  state,
  { filterName, orders, totalNumberOfOrders, nbrOrdersInResponse },
): any => {
  const filterData = pathOr({}, ['orders', 'filters', filterName], state)
  return {
    ...state,
    orders: {
      ...state.orders,
      invoices: mergeDeepRight(
        state.orders.invoices,
        Object.assign(
          {},
          ...orders.map((order) => ({
            [order.orderId]: order,
          })),
        ),
      ),
      filters: {
        ...state.orders.filters,
        [filterName]: {
          ...filterData,
          orderIds: [
            ...pathOr([], ['orderIds'], filterData),
            ...orders.map((order) => order.orderId),
          ],
          isLoadingMore: false,
          totalNumberOfOrders,
          nextOffset: pathOr(0, ['nextOffset'], filterData) + parseInt(nbrOrdersInResponse, 10),
          hasLoaded: true,
          error: false,
        },
      },
    },
  }
}

const loadMoreOrdersFail = (state, { filterName, error }): any => {
  consoleEvents.error(`Failed to load more Klarna orders for filter ${filterName}: ${error}`)
  const filterData = pathOr({}, ['orders', 'filters', filterName], state)
  return {
    ...state,
    orders: {
      ...state.orders,
      filters: {
        ...state.orders.filters,
        [filterName]: {
          ...filterData,
          isLoadingMore: false,
        },
      },
    },
  }
}

// ----------------------------------------
// FETCH MONTHLY INVOICES
// ----------------------------------------
const fetchMonthlyInvoicesStart = (state): any => ({
  ...state,
  monthlyInvoices: {
    ...state.monthlyInvoices,
    isLoading: true,
  },
})

const fetchMonthlyInvoicesSuccess = (
  state,
  { monthlyInvoices, totalNumberOfInvoices, nbrInvoicesInResponse },
): any => {
  return {
    ...state,
    monthlyInvoices: {
      ...state.monthlyInvoices,
      invoices: mergeDeepRight(
        state.monthlyInvoices.invoices,
        Object.assign(
          {},
          ...monthlyInvoices.map((invoice) => ({
            [invoice.id]: invoice,
          })),
        ),
      ),
      filters: {
        [FILTER_NAME.ALL]: {
          ...state.monthlyInvoices.filters.all,
          monthlyInvoiceIds: [
            ...state.monthlyInvoices.filters.all.monthlyInvoiceIds,
            ...monthlyInvoices.map((item) => item.id),
          ],
        },
        [FILTER_NAME.TO_DO]: {
          ...state.monthlyInvoices.filters.toDo,
          monthlyInvoiceIds: [
            ...state.monthlyInvoices.filters.toDo.monthlyInvoiceIds,
            ...monthlyInvoices
              .filter((item) =>
                PAYABLE_MONTHLY_INVOICE_STATUSES.includes(
                  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                  path(['payment_details', 'status'], item),
                ),
              )
              .map((item) => item.id),
          ],
        },
      },
      nextOffset: pathOr(0, ['nextOffset'], state.monthlyInvoices) + nbrInvoicesInResponse,
      totalNumberOfInvoices,
      isLoading: false,
      hasLoaded: true,
    },
  }
}

const fetchMonthlyInvoicesFail = (state, error): any => {
  consoleEvents.error(`Failed to load Klarna monthly invoices: ${error}`)

  return {
    ...state,
    monthlyInvoices: {
      ...state.monthlyInvoices,
      isLoading: false,
      hasLoaded: true,
      error: true,
    },
  }
}

// ----------------------------------------
// FETCH MONTHLY INVOICES
// ----------------------------------------
const fetchMonthlyInvoiceStart = (state): any => state

const fetchMonthlyInvoiceSuccess = (state, invoice): any => ({
  ...state,
  monthlyInvoices: {
    ...state.monthlyInvoices,

    invoices: {
      ...state.monthlyInvoices.invoices,
      [invoice.id]: invoice,
    },
  },
})

const fetchMonthlyInvoiceFail = (state): any => state

// ----------------------------------------
// FETCH MONTHLY INVOICE ORDERS
// ----------------------------------------
const fetchMonthlyInvoiceOrdersStart = (state, collectionId): any =>
  produce(state, (draftState) => {
    const monthlyInvoice = draftState.monthlyInvoices.invoices[collectionId]
    monthlyInvoice.isLoading = true
  })

const fetchMonthlyInvoiceOrdersSuccess = (state, payload): any =>
  produce(state, (draftState) => {
    const { collectionId, klarnaResponse } = payload
    const monthlyInvoice = draftState.monthlyInvoices.invoices[collectionId]
    const { orders } = draftState

    monthlyInvoice.isLoading = false
    monthlyInvoice.isLoaded = true
    monthlyInvoice.orderIds = [
      ...pathOr([], ['orderIds'], monthlyInvoice),
      ...klarnaResponse.orders.map((order) => order.orderId),
    ]
    orders.invoices = mergeDeepRight(
      orders.invoices,
      Object.assign(
        {},
        ...klarnaResponse.orders.map((order) => ({
          [order.orderId]: order,
        })),
      ),
    )
    monthlyInvoice.totalNumberOfOrders = klarnaResponse.totalNumberOfOrders
    monthlyInvoice.nextOffset = monthlyInvoice.nextOffset
      ? monthlyInvoice.nextOffset + LOAD_MORE_ORDERS_PAGE_SIZE
      : MONTHLY_ORDER_LIST_INITIAL_SIZE
  })

const fetchMonthlyInvoiceOrdersFail = (state, collectionId): any =>
  produce(state, (draftState) => {
    const monthlyInvoice = draftState.monthlyInvoices.invoices[collectionId]

    monthlyInvoice.isLoading = false
    monthlyInvoice.isFailed = true
  })

// ----------------------------------------
// OTHER
// ----------------------------------------

// toggleExpandedPath :: will toggle expanded order / monthly invoice / monthly invoice order
const toggleExpandedPath = (state, { id, pathWithFilter }): any => {
  const expandedLens = lensPath(pathWithFilter)
  const isExpanded = compose(equals(id), view(expandedLens))(state)

  return set(expandedLens, isExpanded ? '' : id)(state)
}
const setIsLoadMoreUsed = (state, isLoadMoreUsed): any => ({
  ...state,
  isLoadMoreUsed,
})

const setKlarnaAuthStart = (state): any => ({
  ...state,
  isKlarnaAuthRequested: true,
})

const fetchKlarnaProfileStart = (state): any => {
  return {
    ...state,
    isFetchingKlarnaProfile: true,
  }
}

const fetchKlarnaProfileSuccess = (state, normalizedKlarnaResponse): any => {
  return {
    ...state,
    isFetchingKlarnaProfile: false,
    operations: normalizedKlarnaResponse.operations,
    subscription: normalizedKlarnaResponse.subscription,
  }
}

// setSelectedTab :: collapse open order, monthly invoice, and monthly invoice order of target tab and set selected tab to target tab
const setSelectedTab = (state, tab): any => {
  return compose(
    set(lensPath(['selectedTab']), tab),
    set(lensPath(['monthlyInvoices', 'filters', tab, 'expandedMonthlyInvoiceId']), ''),
    set(lensPath(['monthlyInvoices', 'filters', tab, 'expandedOrderId']), ''),
    set(lensPath(['orders', 'filters', tab, 'expandedOrderId']), ''),
  )(state)
}

const reducer = createReducer((on) => {
  on(actions.fetchOrdersStart, fetchOrdersStart)
  on(actions.fetchOrdersSuccess, fetchOrdersSuccess)
  on(actions.fetchOrdersFail, fetchOrdersFail)

  on(actions.fetchOrderStart, fetchOrderStart)
  on(actions.fetchOrderSuccess, fetchOrderSuccess)
  on(actions.fetchOrderFail, fetchOrderFail)

  on(actions.loadMoreOrdersStart, loadMoreOrdersStart)
  on(actions.loadMoreOrdersSuccess, loadMoreOrdersSuccess)
  on(actions.loadMoreOrdersFail, loadMoreOrdersFail)

  on(actions.fetchMonthlyInvoicesStart, fetchMonthlyInvoicesStart)
  on(actions.fetchMonthlyInvoicesSuccess, fetchMonthlyInvoicesSuccess)
  on(actions.fetchMonthlyInvoicesFail, fetchMonthlyInvoicesFail)

  on(actions.fetchMonthlyInvoiceStart, fetchMonthlyInvoiceStart)
  on(actions.fetchMonthlyInvoiceSuccess, fetchMonthlyInvoiceSuccess)
  on(actions.fetchMonthlyInvoiceFail, fetchMonthlyInvoiceFail)

  on(actions.fetchMonthlyInvoiceOrdersStart, fetchMonthlyInvoiceOrdersStart)
  on(actions.fetchMonthlyInvoiceOrdersSuccess, fetchMonthlyInvoiceOrdersSuccess)
  on(actions.fetchMonthlyInvoiceOrdersFail, fetchMonthlyInvoiceOrdersFail)

  on(actions.toggleExpandedPath, toggleExpandedPath)

  on(actions.setIsLoadMoreUsed, setIsLoadMoreUsed)

  on(actions.setKlarnaAuthStart, setKlarnaAuthStart)

  on(actions.fetchKlarnaProfileStart, fetchKlarnaProfileStart)
  on(actions.fetchKlarnaProfileSuccess, fetchKlarnaProfileSuccess)

  on(actions.setSelectedTab, setSelectedTab)
}, initialState)

export default reducer
